import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Scale, Building, FileText, BookOpen, Copyright, AlertTriangle, Gavel } from 'lucide-react';

export default function LegalPage() {
  return (
    <div data-testid="legal-page" className="min-h-screen bg-background">
      <div className="bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
          <Badge className="bg-white/20 text-white border-0 mb-4">
            Informations juridiques
          </Badge>
          <h1 className="font-serif text-3xl md:text-4xl font-bold mb-4">
            Mentions légales
          </h1>
          <p className="text-lg text-primary-foreground/90 max-w-3xl">
            Informations légales relatives au Journal de pratiques et recherches 
            en psychothérapie (JPRP).
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 md:px-8 py-12 space-y-8">

        <Card>
          <CardHeader>
            <CardTitle className="font-serif text-xl flex items-center gap-2">
              <Building className="w-5 h-5 text-primary" />
              Éditeur de la publication
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <p>
              <span className="font-medium text-foreground">Journal de pratiques et recherches 
              en psychothérapie (JPRP)</span>
            </p>
            <ul className="space-y-1">
              <li><span className="font-medium text-foreground">Directeur de la publication :</span> Victor Lopez-Hernandez</li>
              <li><span className="font-medium text-foreground">Qualité :</span> Psychosociologue, Thérapeute praticien des TCC</li>
              <li><span className="font-medium text-foreground">Affiliation :</span> Cabinet Alésia Thérapie</li>
              <li><span className="font-medium text-foreground">Courriel :</span> <a href="mailto:victor.loher@vmlh-tcc.fr" className="text-primary hover:underline">victor.loher@vmlh-tcc.fr</a></li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-serif text-xl flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Nature de la publication
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <p>
              Le JPRP est une revue scientifique numérique en libre accès (Open Access) dédiée 
              à la publication d'articles scientifiques et cliniques dans le domaine des thérapies 
              comportementales et cognitives (TCC) et de la psychothérapie fondée sur les preuves.
            </p>
            <p>
              La revue publie exclusivement en français et pratique l'évaluation par les pairs 
              (peer review) pour garantir la qualité scientifique de ses publications.
            </p>
            <p>
              <span className="font-medium text-foreground">ISSN :</span> En cours d'attribution
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-serif text-xl flex items-center gap-2">
              <Copyright className="w-5 h-5 text-primary" />
              Propriété intellectuelle et licence
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <p>
              L'ensemble des articles publiés dans le JPRP sont diffusés sous 
              <span className="font-medium text-foreground"> Licence Creative Commons Attribution 4.0 
              International (CC BY 4.0)</span>.
            </p>
            <p>Cette licence permet à quiconque de :</p>
            <ul className="list-disc ml-6 space-y-1">
              <li><span className="font-medium text-foreground">Partager</span> — copier et redistribuer le contenu sur tout support et dans tout format</li>
              <li><span className="font-medium text-foreground">Adapter</span> — remixer, transformer et créer à partir du contenu pour toute utilisation, y compris commerciale</li>
            </ul>
            <p>Sous les conditions suivantes :</p>
            <ul className="list-disc ml-6 space-y-1">
              <li><span className="font-medium text-foreground">Attribution</span> — Vous devez créditer l'auteur, fournir un lien vers la licence et indiquer si des modifications ont été apportées</li>
            </ul>
            <p>
              Les auteurs conservent l'intégralité de leurs droits d'auteur et accordent au JPRP 
              un droit de première publication. Le dépôt d'un manuscrit implique que le travail 
              soumis est original et n'a pas été publié ailleurs.
            </p>
            <p>
              Le nom, le logo et la charte graphique du JPRP sont la propriété exclusive de 
              l'éditeur et ne peuvent être utilisés sans autorisation préalable.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-serif text-xl flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-primary" />
              Politique éditoriale
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <p>
              Le comité éditorial du JPRP veille au respect des normes éthiques en matière 
              de publication scientifique, conformément aux recommandations du 
              <span className="font-medium text-foreground"> Committee on Publication Ethics (COPE)</span>.
            </p>
            <ul className="list-disc ml-6 space-y-1">
              <li>Tout manuscrit fait l'objet d'une évaluation par les pairs en simple aveugle</li>
              <li>Les conflits d'intérêts doivent être déclarés par les auteurs et les évaluateurs</li>
              <li>Le plagiat, la fabrication et la falsification de données sont strictement prohibés</li>
              <li>Les corrections et retraits d'articles sont publiés le cas échéant, conformément aux lignes directrices du COPE</li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-serif text-xl flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-primary" />
              Limitation de responsabilité
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <p>
              Les opinions et résultats exprimés dans les articles publiés dans le JPRP 
              relèvent de la seule responsabilité de leurs auteurs et ne reflètent pas 
              nécessairement la position du comité éditorial ou de l'éditeur.
            </p>
            <p>
              Le JPRP ne saurait être tenu responsable des erreurs ou omissions contenues 
              dans les articles publiés, ni des conséquences découlant de l'utilisation des 
              informations qui y sont présentées.
            </p>
            <p>
              Les informations cliniques publiées ne se substituent en aucun cas à un avis 
              médical ou psychologique professionnel.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-serif text-xl flex items-center gap-2">
              <Gavel className="w-5 h-5 text-primary" />
              Droit applicable
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <p>
              Le présent site et son contenu sont soumis au droit français. 
              Tout litige relatif à l'utilisation de la plateforme sera soumis à la 
              compétence exclusive des tribunaux français.
            </p>
          </CardContent>
        </Card>

        <div className="text-xs text-muted-foreground text-center pt-4">
          Dernière mise à jour : février 2026
        </div>
      </div>
    </div>
  );
}
